// src/components/Dashboard/RoleDashboard/Transfers/ShowInterest.jsx
// Role-side Watchlist (Interest) – VSN Transfers

import React, { useEffect, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { useLocation } from "react-router-dom";
import "./ShowInterest.css";


const VALID_ROLES = ["agent", "scout", "club"];

const ShowInterest = ({ role, userId, agentId, scoutId, clubId }) => {
    const location = useLocation();

    /* -----------------------------------------
       Role awareness (LIGHTWEIGHT)
    ------------------------------------------ */
    const safeRole = String(role || "").toLowerCase();

    const viewerRole = VALID_ROLES.includes(safeRole)
        ? safeRole
        : ((localStorage.getItem("role") || localStorage.getItem("userRole")) || "agent").toLowerCase();



    const roleLabel =
        viewerRole.charAt(0).toUpperCase() + viewerRole.slice(1);

    /* -----------------------------------------
       Resolve effective role entity ID
    ------------------------------------------ */
    const state = location.state || {};

    const effectiveId =
        userId ||
        agentId ||
        scoutId ||
        clubId ||
        state.userId ||
        state.agentId ||
        state.scoutId ||
        state.clubId ||
        null;

    const entitySegment =
        viewerRole === "scout"
            ? "scout"
            : viewerRole === "club"
                ? "club"
                : "agent";

    /* -----------------------------------------
       State
    ------------------------------------------ */
    const [interests, setInterests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    /* -----------------------------------------
       Fetch interests
    ------------------------------------------ */
    useEffect(() => {
        if (!effectiveId) return;

        let ignore = false;

        const fetchInterests = async () => {
            setLoading(true);
            setError("");

            try {
                const path = `/api/${entitySegment}/${effectiveId}/interests`;
                const res = await apiFetch(path);



                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(
                        `Failed to load interests (${res.status}): ${text}`
                    );
                }

                const data = await res.json();

                if (!ignore) {
                    setInterests(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error("❌ Failed to load interests:", err);
                if (!ignore) {
                    setError("Failed to load your watchlist.");
                    setInterests([]);
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        fetchInterests();

        return () => {
            ignore = true;
        };
    }, [effectiveId, entitySegment]);

    /* -----------------------------------------
       Render
    ------------------------------------------ */
    return (
        <div className="interest-container">
            {/* TITLE */}
            <h2 className="interest-title">
                {roleLabel} Watchlist
            </h2>

            <p className="interest-subtext">
                Players you have shown interest in after a blocked signing attempt.
            </p>

            {/* ERROR / EMPTY STATES */}
            {!effectiveId && (
                <p className="interest-empty">
                    No valid role ID found. Please return to your dashboard.
                </p>
            )}

            {error && (
                <p className="interest-empty error">
                    {error}
                </p>
            )}

            {effectiveId && !loading && interests.length === 0 && (
                <p className="interest-empty">
                    Your watchlist is currently empty.
                </p>
            )}

            {loading && (
                <p className="interest-empty">
                    Loading watchlist…
                </p>
            )}

            {/* WATCHLIST */}
            {!loading && interests.length > 0 && (
                <ul className="interest-list">
                    {interests.map((item) => {
                        const player = item.player || {};
                        const playerId = player.id || item.playerId;
                        const playerSlug = player.profileSlug || item.profileSlug || null;


                        const playerName =
                            player.fullName ||
                            player.name ||
                            `Player ${playerId}`;

                        const addedDate = item.createdAt
                            ? new Date(item.createdAt).toLocaleDateString()
                            : "—";

                        return (
                            <li
                                key={item.id || `${playerId}-${item.createdAt}`}
                                className="interest-card"
                            >
                                {/* HEADER */}
                                <div className="interest-card-header">
                                    <span className="player-name">
                                        {playerName}
                                    </span>

                                    <span className="interest-badge">
                                        {roleLabel} Interest
                                    </span>
                                </div>

                                {/* CONTEXT */}
                                <div className="interest-context">
                                    <span className="status-dot yellow" />
                                    Added after attempted signing (player under contract)
                                </div>

                                {/* META */}
                                <div className="interest-meta">
                                    <span className="interest-date">
                                        Added on {addedDate}
                                    </span>
                                </div>

                                {/* ACTIONS */}
                                <div className="interest-actions">
                                    <a
                                        className="interest-link"
                                        href={`/${entitySegment}-dashboard/player-profile/${playerSlug || playerId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View Player Profile
                                    </a>

                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default ShowInterest;




